/**
 * Registra imagePlaneModule para imágenes MPR custom (imagecustom:*)
 * para que CornerstoneTools ReferenceLines pueda calcular intersecciones.
 */
import cornerstone from 'cornerstone-core'
import { readFileIpp, readFileIop, readFileFoR, parseVec } from './seriesLink'

const planeByImageId = new Map()
let providerRegistered = false

function asVec3(arr) {
  if (!arr || arr.length < 3) return null
  return [Number(arr[0]), Number(arr[1]), Number(arr[2])]
}

function add3(a, b) {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
}

function scale3(a, s) {
  return [a[0] * s, a[1] * s, a[2] * s]
}

function cross3(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ]
}

function len3(a) {
  return Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2])
}

function normalize3(a) {
  const L = len3(a)
  if (!L) return [0, 0, 0]
  return [a[0] / L, a[1] / L, a[2] / L]
}

function readIop(image) {
  try {
    if (!image || !image.data || typeof image.data.string !== 'function') return null
    const raw = image.data.string('x00200037')
    if (!raw) return null
    const v = raw.split('\\').map(Number)
    if (v.length < 6 || v.some((n) => Number.isNaN(n))) return null
    return { row: [v[0], v[1], v[2]], col: [v[3], v[4], v[5]] }
  } catch (e) {
    return null
  }
}

function readIpp(image) {
  try {
    if (!image || !image.data || typeof image.data.string !== 'function') return null
    const raw = image.data.string('x00200032')
    if (!raw) return null
    const v = raw.split('\\').map(Number)
    if (v.length < 3 || v.some((n) => Number.isNaN(n))) return null
    return v
  } catch (e) {
    return null
  }
}

function readFrameOfReferenceUID(image) {
  try {
    if (!image || !image.data || typeof image.data.string !== 'function') return undefined
    const raw = image.data.string('x00200052')
    if (!raw) return undefined
    const uid = String(raw).trim()
    return uid || undefined
  } catch (e) {
    return undefined
  }
}

function readFoRFromStack(files, src, mprData) {
  if (mprData && mprData.frameOfReferenceUID) return mprData.frameOfReferenceUID
  const fromSrc = readFrameOfReferenceUID(src)
  if (fromSrc) return fromSrc
  if (!files) return undefined
  for (let i = 0; i < files.length; i++) {
    const u = readFrameOfReferenceUID(files[i] && files[i].image)
    if (u) return u
  }
  return undefined
}

function ensureProvider() {
  if (providerRegistered) return
  // Priority alta: WADO también registra imagePlaneModule (prio 0) y puede
  // tapar overrides UDV (FoR / IPP stub) → ReferenceLines sag↔cor fallan.
  cornerstone.metaData.addProvider((type, imageId) => {
    if (type !== 'imagePlaneModule') return
    return planeByImageId.get(imageId)
  }, 10000)
  providerRegistered = true
}

function firstValidIppEntry(files) {
  if (!files || !files.length) return null
  for (let i = 0; i < files.length; i++) {
    const ipp = readIpp(files[i] && files[i].image)
    if (ipp) return { ipp, index: i }
  }
  return null
}

function lastValidIppEntry(files) {
  if (!files || !files.length) return null
  for (let i = files.length - 1; i >= 0; i--) {
    const ipp = readIpp(files[i] && files[i].image)
    if (ipp) return { ipp, index: i }
  }
  return null
}

/**
 * Extremos IPP del stack (índices 0 y n-1), extrapolando si hace falta,
 * y spacing Z = |IPP_last − IPP_first| / (n − 1).
 *
 * CornerstoneTools recorta la intersección al rectángulo paciente del plano MPR.
 * Si el rowPixelSpacing Z es el in-plane (p.ej. 0.5 mm) en vez del paso real
 * (~1–1.5 mm), el coronal/sagittal “mide” solo una fracción del volumen y las
 * líneas desaparecen a partir de un índice fijo.
 */
export function resolveStackIppEnds(files) {
  const a = firstValidIppEntry(files)
  if (!a || !files || files.length < 1) return null
  const b = lastValidIppEntry(files)
  if (!b || b.index === a.index) {
    return {
      ippFirst: a.ipp,
      ippLast: a.ipp,
      zSpacing: null,
      sliceDir: null,
    }
  }
  const delta = [b.ipp[0] - a.ipp[0], b.ipp[1] - a.ipp[1], b.ipp[2] - a.ipp[2]]
  const dist = len3(delta)
  const steps = b.index - a.index
  if (!(dist > 1e-6) || !(steps > 0)) {
    return {
      ippFirst: a.ipp,
      ippLast: b.ipp,
      zSpacing: null,
      sliceDir: null,
    }
  }
  const zSpacing = dist / steps
  const sliceDir = normalize3(delta)
  const ippFirst = add3(a.ipp, scale3(sliceDir, -a.index * zSpacing))
  const ippLast = add3(a.ipp, scale3(sliceDir, (files.length - 1 - a.index) * zSpacing))
  return { ippFirst, ippLast, zSpacing, sliceDir }
}

/**
 * Rellena mprData.ippFirst / ippLast / zSpacing / sliceDir desde el stack.
 */
export function ensureMprStackGeometry(files, mprData = {}) {
  const ends = resolveStackIppEnds(files)
  if (ends) {
    if (ends.ippFirst) mprData.ippFirst = ends.ippFirst
    if (ends.ippLast) mprData.ippLast = ends.ippLast
    if (ends.sliceDir) mprData.sliceDir = ends.sliceDir
    if (ends.zSpacing != null && ends.zSpacing > 0) {
      mprData.zSpacing = ends.zSpacing
    }
  }
  if (!mprData.frameOfReferenceUID && files && files.length) {
    for (let i = 0; i < files.length; i++) {
      const u = readFrameOfReferenceUID(files[i] && files[i].image)
      if (u) {
        mprData.frameOfReferenceUID = u
        break
      }
    }
  }
  return mprData
}

/**
 * Dirección de apilado Z en espacio paciente a partir de files / mprData.
 */
function resolveSliceDirection(files, mprData, row, col) {
  const normal = normalize3(cross3(row, col))
  if (mprData && mprData.sliceDir && len3(mprData.sliceDir) > 1e-6) {
    return normalize3(mprData.sliceDir)
  }
  const ends = resolveStackIppEnds(files)
  if (ends && ends.sliceDir && len3(ends.sliceDir) > 1e-6) {
    return ends.sliceDir
  }
  const order = mprData && mprData.instanceNumberOrder
  if (order === -1) return scale3(normal, -1)
  return normal
}

function baseIpp(files, mprData) {
  if (mprData && mprData.ippFirst) return mprData.ippFirst
  const ends = resolveStackIppEnds(files)
  if (ends && ends.ippFirst) return ends.ippFirst
  if (!files || !files.length) return null
  return readIpp(files[0] && files[0].image)
}

function lastIpp(files, mprData) {
  if (mprData && mprData.ippLast) return mprData.ippLast
  const ends = resolveStackIppEnds(files)
  if (ends && ends.ippLast) return ends.ippLast
  if (!files || !files.length) return null
  return readIpp(files[files.length - 1] && files[files.length - 1].image)
}

/**
 * Ordena el stack cabeza→pies (más superior primero).
 * Usa ImagePositionPatient: mayor Z paciente (LPS +Z = cabeza) primero.
 * Si Z casi no varía (serie sagital/coronal), no reordena.
 * No usa Instance Number ni el nombre de archivo.
 *
 * @param {Array} files
 * @returns {Array}
 */
export function sortFilesHeadToFeet(files) {
  if (!Array.isArray(files) || files.length < 2) return files || []
  const items = files.map((f, i) => ({
    f,
    i,
    ipp: readIpp(f && f.image),
    loc:
      f && f.sliceLocation != null
        ? Number(f.sliceLocation)
        : f && f.image && f.image.sliceLocation != null
          ? Number(f.image.sliceLocation)
          : null,
  }))
  const withIpp = items.filter((x) => x.ipp)
  if (withIpp.length >= 2) {
    let zMin = Infinity
    let zMax = -Infinity
    for (let k = 0; k < withIpp.length; k++) {
      const z = withIpp[k].ipp[2]
      if (z < zMin) zMin = z
      if (z > zMax) zMax = z
    }
    if (zMax - zMin > 1e-3) {
      items.sort((a, b) => {
        const za = a.ipp ? a.ipp[2] : -Infinity
        const zb = b.ipp ? b.ipp[2] : -Infinity
        if (zb !== za) return zb - za
        return a.i - b.i
      })
      return items.map((x) => x.f)
    }
  }
  const withLoc = items.filter((x) => x.loc != null && !Number.isNaN(x.loc))
  if (withLoc.length >= 2) {
    let lo = Infinity
    let hi = -Infinity
    for (let k = 0; k < withLoc.length; k++) {
      if (withLoc[k].loc < lo) lo = withLoc[k].loc
      if (withLoc[k].loc > hi) hi = withLoc[k].loc
    }
    if (hi - lo > 1e-3) {
      items.sort((a, b) => {
        const la = a.loc != null ? a.loc : -Infinity
        const lb = b.loc != null ? b.loc : -Infinity
        if (lb !== la) return lb - la
        return a.i - b.i
      })
      return items.map((x) => x.f)
    }
  }
  return files.slice()
}

/**
 * Ordena cabeza→pies y recalcula flipZ (no reutilizar el flag anterior).
 * @param {Array} files
 * @param {object} [mprData]
 * @returns {Array}
 */
export function prepareMprStackFiles(files, mprData) {
  const sorted = sortFilesHeadToFeet(files)
  if (mprData) {
    delete mprData.flipZ
    ensureMprStackGeometry(sorted, mprData)
    mprData.flipZ = shouldFlipMprZ(sorted, mprData)
  }
  return sorted
}

/**
 * ¿Voltear Z al pintar MPR? Superior debe quedar arriba.
 * Tras prepareMprStackFiles (cabeza→pies) suele ser false.
 * Si el stack crece hacia superior (último más craneal), hay que invertir filas.
 */
export function shouldFlipMprZ(files, mprData) {
  if (!files || files.length < 2) {
    return !!(mprData && mprData.flipZ)
  }
  if (mprData && typeof mprData.flipZ === 'boolean') return mprData.flipZ

  const src = files[0] && files[0].image
  const iop = readIop(src)
  const ends = resolveStackIppEnds(files)
  const ipp0 = ends && ends.ippFirst
  const ippN = ends && ends.ippLast
  if (ipp0 && ippN) {
    // Definitivo en axial: +Z paciente = cabeza. Índice 0 más craneal ⇒ no voltear.
    if (Math.abs(ippN[2] - ipp0[2]) > 1e-3) return ippN[2] > ipp0[2]
    if (iop) {
      const delta = [ippN[0] - ipp0[0], ippN[1] - ipp0[1], ippN[2] - ipp0[2]]
      const n = normalize3(cross3(iop.row, iop.col))
      const along = delta[0] * n[0] + delta[1] * n[1] + delta[2] * n[2]
      if (Math.abs(along) > 1e-6) return along > 0
    }
  }

  // sliceDistance / instanceNumber
  const d0 = files[0].sliceDistance
  const dN = files[files.length - 1].sliceDistance
  if (d0 != null && dN != null && Math.abs(dN - d0) > 1e-6) {
    return dN > d0
  }
  if (mprData && mprData.instanceNumberOrder === -1) return false
  // Por defecto invertir (caso típico feet-first / stack ascendente)
  return true
}

/**
 * @param {'xz'|'yz'} kind
 * @param {number} sliceIndex  Y (xz) o X (yz) en coords de volumen origen
 * @param {object} opts
 */
export function buildMprImagePlaneModule(kind, sliceIndex, opts = {}) {
  const { files, mprData = {}, originImage, columns, rows } = opts
  const src = originImage || (files && files[0] && files[0].image)
  const iop = readIop(src)
  ensureMprStackGeometry(files, mprData)
  const ipp0 = baseIpp(files, mprData)
  if (!iop || !ipp0) return null

  const rowSp = Number(
    (mprData.rowPixelSpacing != null
      ? mprData.rowPixelSpacing
      : src.rowPixelSpacing) || 1
  )
  const colSp = Number(
    (mprData.columnPixelSpacing != null
      ? mprData.columnPixelSpacing
      : src.columnPixelSpacing) || 1
  )
  // Spacing Z del rectángulo paciente: siempre el paso real IPP, nunca in-plane.
  let zSp = Number(mprData.zSpacing != null ? mprData.zSpacing : 0)
  if (!(zSp > 0)) zSp = colSp
  const sliceDir = resolveSliceDirection(files, mprData, iop.row, iop.col)
  const flipZ = shouldFlipMprZ(files, mprData)
  const idx = Math.max(0, Math.round(Number(sliceIndex) || 0))

  // Top-left del canvas = primera fila de píxeles (superior si flipZ)
  const ippTop = flipZ ? lastIpp(files, mprData) || ipp0 : ipp0
  const colDir = flipZ ? scale3(sliceDir, -1) : sliceDir

  let rowCosines
  let columnCosines
  let columnPixelSpacing
  let rowPixelSpacing
  let imagePositionPatient

  if (kind === 'xz') {
    // Coronal-like: horizontal=X (row), vertical=Z
    rowCosines = iop.row
    columnCosines = colDir
    columnPixelSpacing = colSp
    rowPixelSpacing = zSp > 0 ? zSp : colSp
    imagePositionPatient = add3(ippTop, scale3(iop.col, idx * rowSp))
  } else {
    // Sagittal-like: horizontal=Y (col), vertical=Z
    rowCosines = iop.col
    columnCosines = colDir
    columnPixelSpacing = rowSp
    rowPixelSpacing = zSp > 0 ? zSp : colSp
    imagePositionPatient = add3(ippTop, scale3(iop.row, idx * colSp))
  }

  return {
    frameOfReferenceUID: readFoRFromStack(files, src, mprData),
    rows: rows,
    columns: columns,
    imageOrientationPatient: [...rowCosines, ...columnCosines],
    rowCosines: asVec3(rowCosines),
    columnCosines: asVec3(columnCosines),
    imagePositionPatient: asVec3(imagePositionPatient),
    columnPixelSpacing,
    rowPixelSpacing,
    sliceThickness: zSp,
    spacingBetweenSlices: zSp,
    flipZ,
  }
}

export function registerMprImagePlane(imageId, planeModule) {
  if (!imageId || !planeModule) return
  ensureProvider()
  planeByImageId.set(imageId, planeModule)
}

export function unregisterMprImagePlane(imageId) {
  if (imageId) planeByImageId.delete(imageId)
}

/**
 * imagePlaneModule para un corte nativo del stack (axial). Completa rows/columns/FoR
 * por si el provider WADO no los aporta — ReferenceLines los necesita para el rectángulo.
 */
export function buildNativeImagePlaneModule(image) {
  if (!image) return null
  const iop = readIop(image)
  const ipp = readIpp(image)
  if (!iop || !ipp) return null
  const rowSp = Number(image.rowPixelSpacing) || 1
  const colSp = Number(image.columnPixelSpacing) || 1
  return {
    frameOfReferenceUID: readFrameOfReferenceUID(image),
    rows: image.rows,
    columns: image.columns,
    imageOrientationPatient: [...iop.row, ...iop.col],
    rowCosines: asVec3(iop.row),
    columnCosines: asVec3(iop.col),
    imagePositionPatient: asVec3(ipp),
    columnPixelSpacing: colSp,
    rowPixelSpacing: rowSp,
  }
}

export function registerNativeImagePlane(image) {
  if (!image || !image.imageId) return
  ensureProvider()
  let existing
  try {
    existing = cornerstone.metaData.get('imagePlaneModule', image.imageId)
  } catch (e) {
    existing = null
  }
  const usable =
    existing &&
    existing.rowCosines &&
    existing.columnCosines &&
    existing.imagePositionPatient &&
    existing.rows &&
    existing.columns
  if (usable) return

  const plane = buildNativeImagePlaneModule(image)
  if (!plane) return
  if (existing && existing.frameOfReferenceUID && !plane.frameOfReferenceUID) {
    plane.frameOfReferenceUID = existing.frameOfReferenceUID
  }
  registerMprImagePlane(image.imageId, plane)
}

/**
 * imagePlaneModule desde stub PREVIEW (IPP/IOP en file) o dataset DICOM.
 * Permite Reference Lines antes de bajar el 16-bit.
 */
export function buildFileImagePlaneModule(file, image) {
  const native = buildNativeImagePlaneModule(image)
  if (native) {
    if (file && file.frameOfReferenceUID && !native.frameOfReferenceUID) {
      native.frameOfReferenceUID = file.frameOfReferenceUID
    }
    return native
  }
  if (!file) return null
  const ipp = readFileIpp(file)
  const iopArr = readFileIop(file)
  if (!ipp || !iopArr) return null
  const row = iopArr.slice(0, 3)
  const col = iopArr.slice(3, 6)
  const spacing = parseVec(file.pixelSpacing, 2) || []
  const rowSp =
    Number(image && image.rowPixelSpacing) || Number(spacing[0]) || 1
  const colSp =
    Number(image && image.columnPixelSpacing) || Number(spacing[1]) || rowSp
  const rows = (image && image.rows) || file.rows || 512
  const columns = (image && image.columns) || file.columns || 512
  return {
    frameOfReferenceUID: readFileFoR(file) || undefined,
    rows,
    columns,
    imageOrientationPatient: iopArr.slice(0, 6),
    rowCosines: asVec3(row),
    columnCosines: asVec3(col),
    imagePositionPatient: asVec3(ipp),
    columnPixelSpacing: colSp,
    rowPixelSpacing: rowSp,
  }
}

function vec3Close(a, b, eps = 1e-3) {
  if (!a || !b || a.length < 3 || b.length < 3) return false
  return (
    Math.abs(Number(a[0]) - Number(b[0])) <= eps &&
    Math.abs(Number(a[1]) - Number(b[1])) <= eps &&
    Math.abs(Number(a[2]) - Number(b[2])) <= eps
  )
}

function iopClose(a, b, eps = 1e-4) {
  if (!a || !b || a.length < 6 || b.length < 6) return false
  for (let i = 0; i < 6; i++) {
    if (Math.abs(Number(a[i]) - Number(b[i])) > eps) return false
  }
  return true
}

function planesEquivalent(existing, plane) {
  if (!existing || !plane) return false
  if (
    !existing.rowCosines ||
    !existing.columnCosines ||
    !existing.imagePositionPatient ||
    !existing.rows ||
    !existing.columns
  ) {
    return false
  }
  const ippA = existing.imagePositionPatient
  const ippB = plane.imagePositionPatient
  const ippOk =
    (Array.isArray(ippA) && vec3Close(ippA, ippB)) ||
    (ippA && ippB && vec3Close([ippA.x, ippA.y, ippA.z], ippB))
  if (!ippOk) return false
  const iopA =
    existing.imageOrientationPatient ||
    (existing.rowCosines && existing.columnCosines
      ? [
          existing.rowCosines.x,
          existing.rowCosines.y,
          existing.rowCosines.z,
          existing.columnCosines.x,
          existing.columnCosines.y,
          existing.columnCosines.z,
        ]
      : null)
  return iopClose(iopA, plane.imageOrientationPatient)
}

/**
 * @param {object} file stub manifest / file item
 * @param {object} [image] imagen Cornerstone (puede ser proxy distinto del file)
 * @param {{ force?: boolean }} [opts]
 * @returns {boolean}
 */
export function registerFileImagePlane(file, image, opts = {}) {
  const imageId = (image && image.imageId) || (file && file.imageId)
  if (!imageId) return false
  ensureProvider()
  const plane = buildFileImagePlaneModule(file, image)
  if (!plane) return false

  let existing = null
  try {
    existing = cornerstone.metaData.get('imagePlaneModule', imageId)
  } catch (e) {
    existing = null
  }

  if (!opts.force && planesEquivalent(existing, plane)) return true

  if (existing && existing.frameOfReferenceUID && !plane.frameOfReferenceUID) {
    plane.frameOfReferenceUID = existing.frameOfReferenceUID
  }
  registerMprImagePlane(imageId, plane)
  return true
}

/**
 * PREVIEW scroll: canvas con píxeles proxy pero plano del corte objetivo (IPP/IOP manifest).
 * @param {object} targetFile file del índice buscado
 * @param {object} displayedImage imagen actualmente en el viewport
 * @returns {boolean}
 */
export function registerTargetSlicePlaneOnDisplay(targetFile, displayedImage) {
  if (!targetFile || !displayedImage || !displayedImage.imageId) return false
  return registerFileImagePlane(targetFile, displayedImage, { force: true })
}
